/**
 * @fileoverview Record the domain-specific date/amount context of a demand-sent matter attribute. The backend owns accepted attributes and the parent refreshes their projection. Form submission records evidence; it does not deliver the demand or acknowledge receipt by an adverse party.
 */

import { useContext, useEffect, useState } from "react"
import { AppButton, AppModal, AppCalendar, AppTextInput, AppToggle } from '../shared'
import ToastContext from '../../ToastContext'
import api from "../../api"
import { useSelector } from "react-redux"



function DemandSentAttributeForm({ visible, setVisible, attributes, fetchAttributes }) {
    const matter = useSelector(state => state.caseDetails.matter);
    const showToast = useContext(ToastContext)
    const [dateSent, setDateSent] = useState(null)
    const [trackingInformation, setTrackingInformation] = useState(null)
    const [dateResponseDue, setDateResponseDue] = useState(null)
    const [trelloCardNeeded, setTrelloCardNeeded] = useState(null)
    const [trelloCardTitle, setTrelloCardTitle] = useState(null)
    const [trelloCardDueDate, setTrelloCardDueDate] = useState(null)

    useEffect(() => {
        if (attributes && attributes.length > 0) {
            const findAttribute =

            (name) => {
                const attr = attributes.find(attribute => attribute.Attribute === name);
                return attr ? attr.Value : null;
            };

            const _dateSent = findAttribute('Date Demand Letter Sent') ? new Date(findAttribute('Date Demand Letter Sent')) : null;
            const _dateResponseDue = findAttribute('Date Response Due') ? new Date(findAttribute('Date Response Due')) : null;

            setDateSent(_dateSent);
            setTrackingInformation(findAttribute('Tracking Information'));
            setDateResponseDue(_dateResponseDue);
            setTrelloCardNeeded(findAttribute('Trello Card Needed'));
        }
    }, [attributes]);


    const submit =

    () => {
        console.log('submit')
        const payload = {
            'Date Demand Letter Sent': dateSent,
            'Tracking Information': trackingInformation,
            'Date Response Due': dateResponseDue,
            'Trello Card Needed': trelloCardNeeded,
            'Trello Card Title': trelloCardTitle,
            'Trello Card Due Date': trelloCardDueDate,
            MatterNumber: matter.MatterNumber,
        }

        api.post('/demand_sent_attributes', payload)
            .then(

            response => {
                console.log(response.data)
                fetchAttributes()
                showToast({
                    severity: 'success',
                    summary: 'Successful',
                    detail: 'Demand Sent Attributes Saved'
                })
                setVisible(false)
            })
            .catch(error => {
                console.error('Error:', error)
                showToast({ severity: 'error', summary: 'Error', detail: error.message })
            })
    }

    const footerContent = (
        <div className="case-details-modal__actions">
            <AppButton
                label="Save"
                icon="pi pi-check"
                onClick={submit}
                autoFocus
                disabled={[dateSent, trackingInformation, dateResponseDue, trelloCardNeeded].every((value) => value === null)}
            />
        </div>
    )

    return (
        <>
            <AppModal
                title='Demand Sent Attributes'
                description="Capture delivery details and any follow-up Trello work tied to the demand package."
                visible={visible}
                onClose={() => setVisible(false)}
                width="min(92vw, 760px)"
                footer={footerContent}
                className="case-details-modal"
            >
                <div className="case-details-modal__form">
                    <section className="case-details-modal__section">
                        <div className="case-details-modal__section-header">
                            <div>
                                <h3 className="case-details-modal__section-title">Demand Timeline</h3>
                                <p className="case-details-modal__section-description">
                                    Record when the demand was sent, how it was delivered, and when the response is expected.
                                </p>
                            </div>
                        </div>
                        <div className="case-details-modal__section-body">
                            <div className="case-details-modal__grid">
                                <div className="case-details-modal__field">
                                    <label className="case-details-modal__label">Date Demand Letter Sent</label>
                                    <AppCalendar value={dateSent} onChange={(e) => setDateSent(e.value)} />
                                </div>
                                <div className="case-details-modal__field">
                                    <label className="case-details-modal__label">Date Response Due</label>
                                    <AppCalendar value={dateResponseDue} onChange={(e) => setDateResponseDue(e.value)} />
                                </div>
                                <div className="case-details-modal__field case-details-modal__field--full">
                                    <label className="case-details-modal__label">Tracking Information</label>
                                    <AppTextInput value={trackingInformation} onChange={(e) => setTrackingInformation(e.target.value)} />
                                </div>
                            </div>
                        </div>
                    </section>

                    <section className="case-details-modal__section">
                        <div className="case-details-modal__section-header">
                            <div>
                                <h3 className="case-details-modal__section-title">Follow-Up Task</h3>
                                <p className="case-details-modal__section-description">
                                    Decide whether this demand needs an associated Trello reminder for the response deadline.
                                </p>
                            </div>
                        </div>
                        <div className="case-details-modal__section-body">
                            <div className="case-details-modal__toggle-card">
                                <div className="case-details-modal__toggle-copy">
                                    <div className="case-details-modal__toggle-title">Create a Trello follow-up</div>
                                    <div className="case-details-modal__toggle-description">
                                        Turn this on when the team should see a queued task tied to the demand response.
                                    </div>
                                </div>
                                <AppToggle checked={trelloCardNeeded} onChange={(e) => setTrelloCardNeeded(e.value)} />
                            </div>

                            {trelloCardNeeded && (
                                <div className="case-details-modal__grid">
                                    <div className="case-details-modal__field case-details-modal__field--full">
                                        <label className="case-details-modal__label">Trello Card Title</label>
                                        <AppTextInput value={trelloCardTitle} onChange={(e) => setTrelloCardTitle(e.target.value)} />
                                    </div>
                                    <div className="case-details-modal__field">
                                        <label className="case-details-modal__label">Trello Card Due Date</label>
                                        <AppCalendar value={trelloCardDueDate} onChange={(e) => setTrelloCardDueDate(e.value)} />
                                    </div>
                                </div>
                            )}
                        </div>
                    </section>
                </div>
            </AppModal>
        </>
    )

}

export default DemandSentAttributeForm
